import { DocumentSnapshot, FieldValue } from "@atgof-firebase/firebase";
import React from "react";
import { LanguageContext } from "../common/language";
import { FieldSpecs, SheetCategory, sheetsCollection } from "../common/sheet";
import { IconButton, Text, useTheme } from "react-native-paper";
import { Platform, View } from "react-native";
import { useProject } from "../data/useProject";
import { UserContext } from "../data/userContext";
import { keysForField } from "../common/helpers/fields";
import { episodeForScene } from "../common/episode";
import { Scene } from "../common/scene";
import { ProjectContext } from "../data/projectContext";
import {
  CellRenderProps,
  ScenesTable,
  ScenesTableColumnGroup,
  ScenesTableSection,
  SheetWithApplicands,
  locationColumn,
  sceneColumn,
  sceneNotesColumn,
} from "./ScenesTable";
import { ScriptButton } from "./ScriptButton";
import { SceneButton } from "./SceneButton";
import { MembershipCategory } from "../common/model/project/membership";
import { useUserInkColours } from "../data/useUserInkColours";
import { useFieldSpecs } from "../data/useFieldSpecs";
import { PostProductionSceneOptions } from "./PostProductionSceneOptions";
import { UserColoursKey } from "./UserColoursKey";
import { ApplicandChip } from "./ApplicandChip";
import { SubjectButton } from "./SubjectButton";
import { useReactToPrint } from "react-to-print";
import { EditableAttr, EditableAttrProps } from "./EditableAttr";
import { SubjectSpec } from "@atgof-common/subject";

async function print(printWindow: HTMLIFrameElement) {
  const printContent =
    printWindow.contentDocument || printWindow.contentWindow?.document;
  for (const elt of printContent?.getElementsByTagName("div") ?? []) {
    if (elt.style.overflow === "scroll") elt.style.removeProperty("overflow");
  }
  printWindow.contentWindow?.print();
}

export function useBreakdownTablePrinter() {
  const breakdownTableRef = React.useRef(null);
  const handlePrint = useReactToPrint({
    content: () => breakdownTableRef.current,
    print,
  });
  const printButton = React.useMemo(
    () =>
      Platform.OS === "web" ? (
        <IconButton icon="printer" onPress={handlePrint} />
      ) : null,
    [],
  );
  return { breakdownTableRef, printButton };
}

export type BreakdownTableSection = ScenesTableSection;

type SheetColumn = {
  category: SheetCategory;
  attr: string;
  labelK: string;
  isMulti: boolean;
};

function sheetColumnsForCategory(
  fieldSpecs: FieldSpecs,
  category: MembershipCategory,
): SheetColumn[] {
  const sections = fieldSpecs[category];
  let columns: SheetColumn[] = [];
  const sectionKs = Object.keys(sections ?? {});
  for (const sectionK of sectionKs) {
    for (const field of sections[sectionK]) {
      const { k, labelK } = keysForField(field, category);
      if ("tableColumn" in field && k) {
        const tableColumn = field["tableColumn"];
        if (tableColumn && k && labelK) {
          columns.push({
            category: category as SheetCategory,
            labelK,
            attr: k,
            isMulti: field.multi !== undefined,
          });
        }
      }
    }
  }
  return columns;
}

type EditableSheetOrApplicandCellProps = Omit<
  EditableAttrProps,
  "createIfNeeded" | "refOrSnapshot"
> & {
  snapshot: DocumentSnapshot | undefined;
  scene: Scene;
  category: SheetCategory;
  character: SubjectSpec | undefined;
};

function EditableSheetOrApplicandCell({
  snapshot,
  scene,
  category,
  character,
  ...props
}: EditableSheetOrApplicandCellProps) {
  const characterName = character?.subjectName;
  const { user } = React.useContext(UserContext);
  const project = useProject();
  const ref = React.useMemo(
    () =>
      snapshot?.ref ?? (project ? sheetsCollection(project).doc() : undefined),
    [snapshot?.ref, project],
  );
  const createIfNeeded = React.useMemo(() => {
    return {
      ref,
      data: {
        scene: scene.ref,
        episode: episodeForScene(scene.ref),
        ...(!characterName && scene.sets?.length ? { set: scene.sets[0] } : {}), // TODO
        ...(characterName ? { character: characterName } : {}),
        category,
        deleted: false,
        createdBy: user.ref,
        createdAt: FieldValue.serverTimestamp(),
      },
    };
  }, [ref?.path, scene, user, characterName, category]);
  return (
    <EditableAttr
      {...props}
      refOrSnapshot={snapshot}
      createIfNeeded={createIfNeeded}
    />
  );
}

function EditableSheetCellWithApplicands({
  item,
  ...props
}: {
  item: SheetWithApplicands | undefined;
} & Omit<EditableSheetOrApplicandCellProps, "snapshot">) {
  const { colors } = useTheme();
  return (
    <View style={{ gap: 8 }}>
      {[...(item?.looks ?? []), ...(item?.props ?? [])].map((applicand) => (
        <View
          key={applicand.ref.path}
          style={{
            borderBottomColor: colors.primaryContainer,
            borderBottomWidth: 4,
            paddingVertical: 8,
            gap: 4,
          }}
        >
          <ApplicandChip
            style={{ alignSelf: "center" }}
            applicand={applicand}
          />
          <EditableSheetOrApplicandCell
            {...props}
            snapshot={applicand}
            readonly
          />
        </View>
      ))}
      <EditableSheetOrApplicandCell snapshot={item?.sheet} {...props} />
    </View>
  );
}

export const BreakdownTable = React.forwardRef<
  View,
  {
    sections: BreakdownTableSection[];
  }
>(({ sections }, ref) => {
  const { ph } = React.useContext(LanguageContext);
  const { categories, adminCategories } = React.useContext(ProjectContext);
  const fieldSpecs = useFieldSpecs();
  const inkColours = useUserInkColours();
  const columnGroups = React.useMemo<ScenesTableColumnGroup[]>(
    () => [
      {
        key: "core",
        kind: "scene",
        columns: [
          sceneColumn(ph, (scene) => (
            <React.Fragment>
              {categories.includes("postProduction") ? (
                <PostProductionSceneOptions
                  scene={scene}
                  style={{ margin: 0, padding: 0 }}
                />
              ) : null}
              <SceneButton scene={scene} />
              <ScriptButton parent={scene} />
            </React.Fragment>
          )),
          locationColumn(ph),
          ...(categories.includes("pa")
            ? [sceneNotesColumn(ph, inkColours)]
            : []),
        ],
      },
      {
        key: "character",
        kind: "character",
        columns: [
          {
            key: "character",
            label: ph("character") as string,
            render: ({ item, scene }: CellRenderProps<SubjectSpec>) => (
              <SubjectButton
                {...item}
                episodePath={episodeForScene(scene.ref)?.path}
                fallback={<Text>{item.subjectName}</Text>}
              />
            ),
          },
        ],
      },
      ...categories.map((category) => {
        return {
          key: category,
          kind: { sheetCategory: category as SheetCategory }, // TODO Shouldn't really cast this here
          label: ph(category) as string,
          columns: sheetColumnsForCategory(fieldSpecs, category).map(
            ({ labelK, attr, isMulti }) => ({
              key: attr,
              label: ph(labelK) as string,
              render: ({
                item,
                scene,
                category,
                character,
              }: CellRenderProps<SheetWithApplicands | undefined>) =>
                category ? (
                  <EditableSheetCellWithApplicands
                    item={item}
                    readonly={!adminCategories.includes(category)}
                    character={character}
                    attr={attr}
                    isMulti={isMulti}
                    scene={scene}
                    category={category}
                    inkColours={inkColours ?? []}
                  />
                ) : null,
            }),
          ),
        };
      }),
    ],
    [ph, fieldSpecs, categories, inkColours],
  );
  return (
    <View ref={ref} style={{ flex: 1, gap: 4 }}>
      <UserColoursKey value={inkColours} />
      <ScenesTable sections={sections} columnGroups={columnGroups} />
    </View>
  );
});
